import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Vendors from './pages/Vendors';
import VendorStore from './pages/VendorStore';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VendorRegister from './pages/auth/VendorRegister';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Orders from './pages/Orders';

// Vendor Dashboard
import VendorDashboard from './pages/vendor/Dashboard';
import VendorProducts from './pages/vendor/Products';
import VendorOrders from './pages/vendor/Orders';
import VendorProfile from './pages/vendor/Profile';
import AddProduct from './pages/vendor/AddProduct';
import EditProduct from './pages/vendor/EditProduct';
import POS from './pages/vendor/POS';
import InventoryMovements from './pages/vendor/InventoryMovements';
import Reports from './pages/vendor/Reports';
import StoreCard from './pages/vendor/StoreCard';
import Settings from './pages/vendor/Settings';

// Admin Dashboard
import AdminDashboard from './pages/admin/Dashboard';
import AdminCategories from './pages/admin/Categories';
import AdminUsers from './pages/admin/Users';
import AdminVendors from './pages/admin/Vendors';
import AdminProducts from './pages/admin/Products';
import AdminOrders from './pages/admin/Orders';
import AdminReviews from './pages/admin/Reviews';
import AdminSettlements from './pages/admin/Settlements';
import AdminSettings from './pages/admin/Settings';

// Context
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SettingsProvider } from './context/SettingsContext';

// Protected Routes
import ProtectedRoute from './components/ProtectedRoute';
import VendorRoute from './components/VendorRoute';
import AdminRoute from './components/AdminRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <GoogleOAuthProvider clientId="YOUR_GOOGLE_CLIENT_ID">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <SettingsProvider>
              <Router>
                <AppContent />
              </Router>
            </SettingsProvider>
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin/');
  const isVendorPage = location.pathname.startsWith('/vendor/');
  const isDashboardPage = isAdminPage || isVendorPage;

  return (
    <div className="app-container min-h-screen bg-gray-50 font-arabic">
      <header className="app-header">
        <Navbar />
      </header>
      
      <main className="app-main">
        <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/vendors" element={<Vendors />} />
                  <Route path="/vendors/store/:slug" element={<VendorStore />} />
                  <Route path="/store/:slug" element={<VendorStore />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/vendor/register" element={<VendorRegister />} />
                  
                  {/* Protected Routes */}
                  <Route path="/cart" element={
                    <ProtectedRoute>
                      <Cart />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  } />
                  
                  {/* Vendor Routes */}
                  <Route path="/vendor/dashboard" element={
                    <VendorRoute>
                      <VendorDashboard />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/products" element={
                    <VendorRoute>
                      <VendorProducts />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/products/add" element={
                    <VendorRoute>
                      <AddProduct />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/products/edit/:id" element={
                    <VendorRoute>
                      <EditProduct />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/products/:productId/inventory" element={
                    <VendorRoute>
                      <InventoryMovements />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/orders" element={
                    <VendorRoute>
                      <VendorOrders />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/pos" element={
                    <VendorRoute>
                      <POS />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/reports" element={
                    <VendorRoute>
                      <Reports />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/store-card" element={
                    <VendorRoute>
                      <StoreCard />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/profile" element={
                    <VendorRoute>
                      <VendorProfile />
                    </VendorRoute>
                  } />
                  <Route path="/vendor/settings" element={
                    <VendorRoute>
                      <Settings />
                    </VendorRoute>
                  } />

                  {/* Admin Routes */}
                  <Route path="/admin/dashboard" element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } />
                  <Route path="/admin/categories" element={
                    <AdminRoute>
                      <AdminCategories />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users" element={
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vendors" element={
                    <AdminRoute>
                      <AdminVendors />
                    </AdminRoute>
                  } />
                  <Route path="/admin/products" element={
                    <AdminRoute>
                      <AdminProducts />
                    </AdminRoute>
                  } />
                  <Route path="/admin/orders" element={
                    <AdminRoute>
                      <AdminOrders />
                    </AdminRoute>
                  } />
                  <Route path="/admin/reviews" element={
                    <AdminRoute>
                      <AdminReviews />
                    </AdminRoute>
                  } />
                  <Route path="/admin/settlements" element={
                    <AdminRoute>
                      <AdminSettlements />
                    </AdminRoute>
                  } />
                  <Route path="/admin/settings" element={
                    <AdminRoute>
                      <AdminSettings />
                    </AdminRoute>
                  } />
                </Routes>
              </main>
              
              <footer className={`app-footer ${isDashboardPage ? 'lg:pr-64' : ''}`}>
                <Footer />
              </footer>
              
              <Toaster
                position="top-center"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                    fontFamily: 'Cairo, sans-serif',
                  },
                  success: {
                    style: {
                      background: '#16a34a',
                    },
                  },
                  error: {
                    style: {
                      background: '#dc2626',
                    },
                  },
                }}
              />
            </div>
  );
}

export default App;